import React, { useEffect, useState } from 'react';
import { Card, Container, Row, Alert, Button } from 'react-bootstrap';
import { get, isEmpty } from 'lodash';
import DynamicForm from 'screens/common/dynamicForm';
import { toast } from 'react-toastify';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowLeft, faSync } from '@fortawesome/free-solid-svg-icons';
import FalconCardHeader from 'components/common/FalconCardHeader';
import { useNavigate } from 'react-router-dom';
import { farmerReportApi } from 'api/endpoints/farmers';
import { useParams } from 'react-router-dom';
import { formSubmissionToast } from 'helpers/utils';
import { getTenantFromToken } from 'utils/routeUtils';
import offlineDBService from 'db/dexieDB';
import useOnlineStatus from 'hooks/useOnlineStatus';

function EditFarmer(props) {
  const tenant = getTenantFromToken();
  const tns_tenant = '03534add672340809e2c70314b1ce20e';
  const navigate = useNavigate();
  const [data, setData] = useState({});
  const [offlineMode, setOfflineMode] = useState(false);
  const [loading, setLoading] = useState(true);
  const [farmerData, setFarmerData] = useState(null);
  const { id } = useParams();
  const farmerId = id;

  // Get online status
  const isOnline = useOnlineStatus();

  // Function to set form field values with farmer data
  // Final populateFormWithFarmerData function with comprehensive numeric type handling

  const populateFormWithFarmerData = (formSchema, farmerData) => {
    if (!formSchema || !formSchema.fields || !farmerData) {
      console.error('Missing required data to populate form');
      return formSchema;
    }

    // Clone the form schema to avoid modifying the original
    const updatedForm = JSON.parse(JSON.stringify(formSchema));

    // Update the form fields with farmer data
    updatedForm.fields = updatedForm.fields.map(field => {
      // Process the field even if there's no farmer data for it
      // as we need to handle default values in the schema itself

      // First, let's fix any numeric defaultValue that's stored as a string in the field definition
      if (
        field.type === 'dropdown' ||
        field.type === 'select' ||
        field.type === 'number'
      ) {
        if (
          field.defaultValue !== undefined &&
          typeof field.defaultValue === 'string' &&
          !isNaN(field.defaultValue)
        ) {
          // This looks like a numeric value stored as a string in the field definition
          // console.log(
          //   `Converting default value for ${field.id} from "${
          //     field.defaultValue
          //   }" to number ${Number(field.defaultValue)}`
          // );
          field.defaultValue = Number(field.defaultValue);
        }
      }

      // Now get the value from farmer data if it exists
      const fieldValue = farmerData[field.id];

      // If no farmer data for this field, return the field with potentially fixed defaultValue
      if (fieldValue === undefined) {
        return field;
      }

      // Handle table fields
      if (field.table) {
        const tableData = Array.isArray(fieldValue) ? fieldValue : [];
        return {
          ...field,
          table: {
            ...field.table,
            rows: tableData.length > 0 ? tableData : field.table.rows
          }
        };
      }
      // Handle dropdown fields - ensure they are converted to numbers
      else if (field.type === 'dropdown' || field.type === 'select') {
        let convertedValue = fieldValue;

        // Convert string to number if it looks like a number
        if (typeof fieldValue === 'string' && !isNaN(fieldValue)) {
          convertedValue = Number(fieldValue);
          // console.log(
          //   `Converting field ${field.id} value from string "${fieldValue}" to number ${convertedValue}`
          // );
        }

        return {
          ...field,
          defaultValue: convertedValue
        };
      }
      // Handle numeric input fields
      else if (field.type === 'number') {
        let numValue = fieldValue;

        // Convert string to number if needed
        if (typeof fieldValue === 'string' && !isNaN(fieldValue)) {
          numValue = Number(fieldValue);
          // console.log(
          //   `Converting number field ${field.id} from string "${fieldValue}" to number ${numValue}`
          // );
        }

        return {
          ...field,
          defaultValue: numValue
        };
      }
      // Handle all other fields
      else {
        return {
          ...field,
          defaultValue: fieldValue
        };
      }
    });

    // Additional pass to normalize dependent dropdown fields default values
    // This is needed because some dropdowns might depend on other dropdowns
    updatedForm.fields.forEach(field => {
      if (
        (field.type === 'dropdown' || field.type === 'select') &&
        field.defaultValue !== undefined
      ) {
        // Ensure dropdown values are numbers if they look like numbers
        if (
          typeof field.defaultValue === 'string' &&
          !isNaN(field.defaultValue)
        ) {
          field.defaultValue = Number(field.defaultValue);
          console.log(
            `Final conversion for ${field.id} to number: ${field.defaultValue}`
          );
        }
      }
    });

    return updatedForm;
  };

  // Fetch form data and farmer data
  const fetchFormData = async () => {
    setLoading(true);
    try {
      let formSchema = null;
      let farmer = null;

      // Try to get farmer from offline DB first
      try {
        farmer = await offlineDBService.getFarmerById(
          parseInt(farmerId, 10) || farmerId
        );
        console.log('Farmer from offline DB:', farmer);
        setFarmerData(farmer);
      } catch (dbError) {
        console.error('Error fetching farmer from local DB:', dbError);
      }

      // If we have an offline farmer, we're in offline mode
      const isOfflineFarmer = farmer && farmer.synced === false;
      setOfflineMode(isOfflineFarmer || !isOnline);

      if (isOnline && !isOfflineFarmer) {
        // Online mode - get form from API
        const formResponse = await farmerReportApi.getEditFarmerForm(
          tns_tenant,
          farmerId
        );
        formSchema = formResponse?.data?.results;
        setData(formSchema);
      } else {
        // Offline mode - get form schema from local DB
        const formDef = await offlineDBService.getFormDefinition(
          'farmer-create'
        );

        if (formDef && formDef.formData) {
          console.log('Form definition found in local DB:', formDef.formData);
          formSchema = formDef.formData;

          // If we have offline farmer data, populate the form with it
          if (farmer) {
            const populatedForm = populateFormWithFarmerData(
              formSchema,
              farmer
            );
            setData(populatedForm);
          } else {
            setData(formSchema);
            toast.warning(
              'Could not find farmer data. Some fields may be empty.'
            );
          }
        } else {
          console.error('No form definition found in local DB');
          toast.error('Could not load the form for offline editing.');
        }
      }
    } catch (error) {
      console.error('Error fetching form data:', error);
      toast.error('Failed to load form. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchFormData();
  }, [isOnline, tns_tenant, farmerId]);

  // Fixed handleFormSubmit function for EditFarmer.js

  // Updated handleFormSubmit function for EditFarmer.js with edge case handling
  const handleFormSubmit = async formData => {
    console.log('Form data submitted:', formData);
    try {
      // Check if user is online - if online, always try API method first
      if (isOnline) {
        console.log('User is online - proceeding with API submission');

        // Check if this farmer exists in local DexieDB
        let localFarmer = null;
        let isUnsynced = false;

        try {
          localFarmer = await offlineDBService.getFarmerById(
            parseInt(farmerId, 10) || farmerId
          );

          if (localFarmer) {
            console.log('Found farmer in local DB:', localFarmer);
            // Check if the farmer is unsynced (created offline)
            isUnsynced = localFarmer.synced === false;

            if (isUnsynced) {
              console.log('This is an unsynced farmer (created offline)');
            } else {
              console.log('This is a synced farmer (from main DB)');
            }
          }
        } catch (dbError) {
          console.log(
            'No local farmer found with this ID, assuming it exists only in main DB'
          );
        }

        try {
          let response;

          // EDGE CASE: If the farmer exists locally and is unsynced (synced === false),
          // treat it as a new farmer creation rather than an edit
          if (localFarmer && isUnsynced) {
            console.log(
              'Creating new farmer in main DB for this unsynced local farmer'
            );

            // Prepare data - merge form data with original data to ensure all fields are present
            const dataToCreate = {
              ...localFarmer, // Include all original data
              ...formData, // Override with new form data
              // Remove local DB specific fields that shouldn't go to API
              id: undefined, // Don't send local ID to API
              synced: undefined,
              syncAttempted: undefined,
              syncError: undefined
            };

            // Call create API instead of edit
            response = await farmerReportApi.sendFarmerCreate(
              tns_tenant,
              dataToCreate
            );
          } else {
            // Normal case: Edit an existing farmer in the main DB
            console.log('Editing existing farmer in main DB');
            response = await farmerReportApi.sendFarmerEdit(
              tns_tenant,
              farmerId,
              formData
            );
          }

          console.log('API response:', response);

          // Check if the response indicates success
          const isSuccessful =
            response &&
            (response.data?.success === true ||
              (response.status &&
                response.status >= 200 &&
                response.status < 300));

          // If API save was successful and we have a local copy, handle related farms and then remove it from DexieDB
          if (isSuccessful && localFarmer) {
            // Extract the permanent ID from the API response
            const permanentFarmerId =
              response.data?.id ||
              response.data?.farmer_id ||
              response.id ||
              response.farmerId;

            // Only proceed with farm updates if we have a permanent ID that's different from the local ID
            if (permanentFarmerId && permanentFarmerId !== localFarmer.id) {
              console.log(
                `Got permanent farmer ID ${permanentFarmerId} from API`
              );

              // Check if there are any farms using this farmer's temporary ID
              try {
                const relatedFarms = await offlineDBService.getFarmsByFarmerId(
                  localFarmer.id
                );

                if (relatedFarms && relatedFarms.length > 0) {
                  console.log(
                    `Found ${relatedFarms.length} farm(s) with farmer ID ${localFarmer.id} that need updating`
                  );

                  // Update each farm with the permanent farmer ID
                  for (const farm of relatedFarms) {
                    try {
                      console.log(
                        `Updating farm ${farm.id} to use permanent farmer ID ${permanentFarmerId}`
                      );
                      await offlineDBService.updateFarmFarmerId(
                        farm.id,
                        permanentFarmerId
                      );
                    } catch (farmUpdateError) {
                      console.error(
                        `Error updating farm ${farm.id} with permanent farmer ID:`,
                        farmUpdateError
                      );
                    }
                  }

                  toast.info(
                    `Updated ${relatedFarms.length} farm(s) to use the permanent farmer ID`
                  );
                } else {
                  console.log(
                    `No farms found with farmer ID ${localFarmer.id}`
                  );
                }
              } catch (farmCheckError) {
                console.error(
                  'Error checking for related farms:',
                  farmCheckError
                );
              }
            }

            try {
              console.log(
                `Attempting to delete local farmer with ID ${localFarmer.id}`
              );
              await offlineDBService.deleteFarmer(localFarmer.id);
              console.log(
                `Successfully removed farmer with ID ${localFarmer.id} from local DB`
              );
              toast.info('Data saved to server and removed from local storage');
            } catch (deleteError) {
              console.error(
                'Error removing farmer from local DB:',
                deleteError
              );
              toast.warning('Saved to server but failed to remove local copy');
            }
          } else if (isSuccessful) {
            // Regular success message
            toast.success('Farmer data updated successfully');
          } else {
            // Some other response format, use the helper
            formSubmissionToast(response, toast, 'farmer', true);
          }

          // Navigate back to the main page
          navigate(`/${tenant}/farmerReport/`);
        } catch (apiError) {
          console.error('API error:', apiError);
          formSubmissionToast(apiError, toast, 'farmer', true);

          // If online API call fails, we could optionally fall back to saving locally
          if (
            window.confirm(
              'Failed to save to server. Would you like to save locally instead?'
            )
          ) {
            saveLocally(formData);
          }
        }
      } else {
        // User is offline, save to local DB
        console.log('User is offline - saving to local DB');
        saveLocally(formData);
        toast.success('Farmer data saved locally. Will sync when online.');
        navigate(`/offline-data/`);
      }
    } catch (err) {
      console.error('Unexpected error:', err);
      toast.error('An unexpected error occurred while updating the farmer.');
    }
  };

  // Helper function to save data locally
  const saveLocally = async formData => {
    try {
      console.log(`Attempting to update farmer with ID: ${farmerId}`);

      // If we have the original farmer data, merge it with the form data to ensure
      // we don't lose fields that weren't in the form
      let dataToUpdate = { ...formData };

      if (farmerData) {
        console.log('Merging with existing farmer data');
        // Start with original data and override with form data
        // This ensures we preserve fields that aren't in the form
        dataToUpdate = {
          ...farmerData,
          ...formData,
          // Explicitly set these fields to ensure they're updated
          synced: false,
          updatedAt: new Date().toISOString()
        };
      }

      // Handle the case where the form data might have an 'id' field
      // that conflicts with the database ID
      if (dataToUpdate.id && dataToUpdate.id !== farmerId) {
        console.log(
          `Form data has different ID (${dataToUpdate.id}) than farmer ID (${farmerId})`
        );
        // Save the form's ID field under a different name if needed
        dataToUpdate.formId = dataToUpdate.id;
      }

      // Set the ID to match the route parameter
      dataToUpdate.id = farmerId;

      console.log('Final data to update:', dataToUpdate);

      try {
        const result = await offlineDBService.updateFarmer(
          farmerId,
          dataToUpdate
        );
        console.log('Update result:', result);

        toast.success('Farmer data updated locally. Will sync when online.');
        navigate(`/offline-data/`);
      } catch (updateError) {
        // Show the error to the user directly instead of trying to create a new record
        console.error('Error updating farmer:', updateError);
        toast.error('Failed to update farmer: ' + updateError.message);

        // Stay on the form page so user can correct issues
        // Don't navigate away or create new records
      }
    } catch (error) {
      console.error('Error in saveLocally function:', error);
      toast.error('Failed to process farmer data: ' + error.message);
    }
  };

  const header = (
    <div className="d-flex align-items-center">
      <div
        className="me-2 my-auto theme-control-toggle"
        onClick={() =>
          offlineMode
            ? navigate(`/offline-data`)
            : navigate(`/${tenant}/farmerReport`)
        }
      >
        <div className="theme-control-toggle-label">
          <FontAwesomeIcon icon={faArrowLeft} className="fs-0" />
        </div>
      </div>
      <span>Edit Farmer Details</span>
      {offlineMode && (
        <span className="ms-2 badge bg-warning">Offline Mode</span>
      )}
    </div>
  );

  console.log(data, 'datadata');

  return (
    <Container>
      {offlineMode && (
        <Row className="mb-3">
          <Alert variant="warning">
            <div className="d-flex align-items-center">
              <FontAwesomeIcon
                icon={faSync}
                className="me-2"
                spin={!isOnline}
              />
              <div>
                You are editing in offline mode. Changes will be saved locally
                and synchronized when you're back online.
              </div>
            </div>
          </Alert>
        </Row>
      )}
      <Row>
        <Card className="mb-3">
          <Card.Header>
            <FalconCardHeader
              title={header}
              className="ps-lg-4"
              titleTag="h5"
              titleClass="mb-0"
              light
            />
          </Card.Header>
          <Card.Body>
            {loading ? (
              <div className="text-center p-5">
                <div className="spinner-border" role="status">
                  <span className="visually-hidden">Loading...</span>
                </div>
                <p className="mt-3">Loading form data...</p>
              </div>
            ) : !isEmpty(data) ? (
              <DynamicForm
                data={data}
                header={true}
                onSubmit={handleFormSubmit}
                mode="edit"
              />
            ) : (
              <Alert variant="danger">
                Could not load the farmer data. Please try again later.
              </Alert>
            )}
          </Card.Body>
        </Card>
      </Row>
    </Container>
  );
}

export default EditFarmer;
